import { useState, useMemo } from 'react'
import { Stack, TextField, MenuItem, Box, Select, InputLabel, FormControl, Button, InputAdornment } from '@mui/material'
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'

import { i18WithDefault as t } from '@shared/locales'
import { Types } from 'service-api'
import { getUniqueBenefitOffers } from './helper'
import { format } from 'date-fns'
import { CalendarIconAvatar } from 'icon_avatar/icon_avatar'

export const BenefitConsumptionTableFilters = ({
  benefitConsumptionRecords,
  onApplyFilters,
  onClearFilters,
  hideBenefitFilter,
}: {
  benefitConsumptionRecords?: Types.APIBenefitConsumptionRecordAdminDtoResponse
  onApplyFilters?: (filters: { benefitOffer: string; startDate?: Date; endDate?: Date }) => void
  onClearFilters?: () => void
  hideBenefitFilter?: boolean
}) => {
  const [selectedBenefitOffer, setSelectedBenefitOffer] = useState(t('global.label.all'))
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(undefined)
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(undefined)

  const handleApplyFilters = () => {
    onApplyFilters?.({
      benefitOffer: selectedBenefitOffer,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
    })
  }

  const resetFilters = () => {
    setSelectedStartDate(undefined)
    setSelectedEndDate(undefined)
    setSelectedBenefitOffer(t('global.label.all'))
    onClearFilters?.()
  }

  const benefitOffers = useMemo(() => getUniqueBenefitOffers(benefitConsumptionRecords), [benefitConsumptionRecords])

  return (
    <Box sx={{ mb: 4 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          {!hideBenefitFilter && (
            <Box sx={{ width: 250 }}>
              <FormControl fullWidth>
                <InputLabel id={`benefitId-label`} sx={{ fontSize: 10 }}>
                  {t('global.label.benefit')}
                </InputLabel>
                <Select
                  name="benefitOffer"
                  labelId="benefitOffer-label"
                  label={t('global.label.benefit')}
                  value={selectedBenefitOffer}
                  onChange={(e) => {
                    setSelectedBenefitOffer(e.target.value)
                  }}
                >
                  <MenuItem value={t('global.label.all')}>{t('global.label.all')}</MenuItem>
                  {benefitOffers.map((offer, idx) => (
                    <MenuItem key={`${offer.value}_${idx}`} value={offer.value}>
                      {offer.label} {t('membership.benefit.discount.small')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateRangePicker
              inputFormat="MM/dd/yyyy"
              value={[selectedStartDate ? selectedStartDate : null, selectedEndDate ? selectedEndDate : null]}
              onChange={(newValue) => {
                setSelectedStartDate(newValue[0] || undefined)
                setSelectedEndDate(newValue[1] || undefined)
              }}
              renderInput={(startProps, _endProps) => {
                let displayValue = ''
                if (selectedStartDate && selectedEndDate) {
                  displayValue = `${format(selectedStartDate, 'MM/dd/yyyy')} - ${format(selectedEndDate, 'MM/dd/yyyy')}`
                } else if (selectedStartDate) {
                  displayValue = format(selectedStartDate, 'MM/dd/yyyy')
                }
                return (
                  <TextField
                    {...startProps}
                    label={t('membership.benefit.redeemedDate')}
                    sx={{ width: 260 }}
                    InputProps={{
                      ...startProps.InputProps,
                      endAdornment: (
                        <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                          <CalendarIconAvatar />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      ...startProps.inputProps,
                      placeholder: 'MM/DD/YYYY',
                      value: displayValue,
                    }}
                  />
                )
              }}
            />
          </LocalizationProvider>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={handleApplyFilters} color="secondary" sx={{ py: 1, px: 2 }}>
            {t('global.label.applyFilters')}
          </Button>
          <Button variant="outlined" onClick={resetFilters} color="primary" sx={{ py: 1, px: 2 }}>
            {t('global.label.clearFilters')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
